import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { brand } from "../../theme/brand";
import { formatSyncTime } from "../../utils/formatSyncTime";
import { AccountBadge, resolveAccountUiState } from "../AccountBadge";

type NavItem = {
  key: string;
  label: string;
  icon: ReactNode;
};

type ProfileAction = {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
};

type AppShellProps = {
  navItems: NavItem[];
  activeNavKey: string;
  onSelectNav: (key: string) => void;
  headerSprintLabel: string;
  headerSprintModeTag?: string | null;
  profileName: string;
  profileEmail?: string | null;
  profileActions: ProfileAction[];
  isAuthenticated: boolean;
  avatarKey?: string;
  syncStatus?: "local" | "synced" | "syncing" | "offline";
  lastSyncAt?: string | null;
  syncError?: string | null;
  onSettings?: () => void;
  hideSettingsButton?: boolean;
  headerRightExtras?: ReactNode;
  headerLeftOverride?: ReactNode;
  headerBottomContent?: ReactNode;
  /** When provided, completely replaces the header-top row content (e.g. demo mode). */
  headerTopOverride?: ReactNode;
  sprintSummaryCard?: {
    statusLabel: string;
    statusTone: { border: string; bg: string; text: string };
    phaseLabel?: string | null;
    startLabel: string;
    goalLabel: string;
    nowLabel: string;
    deltaLabel: string;
    onClick: () => void;
  };
  appVersion?: string;
  termsUrl?: string;
  privacyUrl?: string;
  supportUrl?: string;
  /** Items to show in the mobile bottom navigation bar (4 items recommended) */
  mobileBottomNavItems?: NavItem[];
  /** Callback for the center "Log" button in the mobile bottom nav */
  mobileLogButton?: { onPress: () => void };
  /** Active key to highlight in the mobile bottom nav */
  activeMobileNavKey?: string;
  /** Primary sidebar action rendered below nav with divider */
  sidebarPrimaryAction?: {
    label: string;
    onPress: () => void;
    icon?: ReactNode;
  };
  children: ReactNode;
};

// Small colored dot with tooltip (dropdown only — badge uses CloudStatusIcon)
function StatusDot({ color, title }: { color: string; title: string }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: 999,
        background: color,
        flexShrink: 0,
        cursor: "default",
      }}
    />
  );
}

export function AppShell({
  navItems,
  activeNavKey,
  onSelectNav,
  headerSprintLabel,
  headerSprintModeTag,
  profileName,
  profileEmail,
  profileActions,
  isAuthenticated,
  avatarKey = "",
  syncStatus = "local",
  lastSyncAt,
  syncError,
  onSettings,
  hideSettingsButton = false,
  headerRightExtras,
  headerLeftOverride,
  headerBottomContent,
  headerTopOverride,
  sprintSummaryCard,
  appVersion,
  termsUrl,
  privacyUrl,
  supportUrl,
  mobileBottomNavItems,
  mobileLogButton,
  activeMobileNavKey,
  sidebarPrimaryAction,
  children,
}: AppShellProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!profileRef.current?.contains(e.target as Node)) setProfileOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeNavKey]);

  const initials = useMemo(() => {
    const split = profileName.trim().split(/\s+/).filter(Boolean);
    if (!split.length) return "A";
    return split.slice(0, 2).map((v) => v[0]?.toUpperCase() ?? "").join("") || "A";
  }, [profileName]);

  // "Local" → "Guest" for unauthenticated users with no display name
  const resolvedDisplayName = useMemo(() => {
    if (!isAuthenticated && (profileName === "Local" || profileName === "")) return "Guest";
    return profileName;
  }, [isAuthenticated, profileName]);

  const { statusText, color: dotColor } = useMemo(
    () => resolveAccountUiState(syncStatus),
    [syncStatus],
  );

  const hasMobileNav = !!mobileBottomNavItems?.length;
  const leftNavItems = mobileBottomNavItems?.slice(0, 2) ?? [];
  const rightNavItems = mobileBottomNavItems?.slice(2) ?? [];

  return (
    <div className="app-shell">
      <style>{`
        .app-shell {
          --layer-settings-subnav: 20;
          --layer-app-header: 30;
          --layer-account-dropdown: 40;
          min-height: 100dvh;
          height: 100dvh;
          overflow: hidden;
          background: ${brand.background.primary};
          color: #e5e7eb;
          display: grid;
          grid-template-columns: 252px 1fr;
          font-family: var(--font-sans);
        }
        .app-shell-sidebar { position: sticky; top: 0; align-self: start; height: 100vh; border-right: 1px solid ${brand.border.subtle}; background: ${brand.background.primary}; padding: 10px 8px 12px; display: grid; grid-template-rows: auto auto; align-content: start; gap: 10px; }
        .app-shell-main { min-width: 0; min-height: 0; height: 100dvh; display: grid; grid-template-rows: auto 1fr auto; }
        .app-shell-main-inner {
          --main-inner-max-width: 1360px;
          width: 100%;
          max-width: var(--main-inner-max-width);
          margin: 0 auto;
          padding: 0 18px;
          min-width: 0;
        }
        /* Layer contract: settings sticky nav < header < account dropdown */
        .app-shell-header { border-bottom: 1px solid ${brand.border.subtle}; padding: max(14px, env(safe-area-inset-top)) 0 8px; display: grid; grid-template-rows: auto auto; gap: 10px; position: sticky; top: 0; z-index: var(--layer-app-header); background: rgba(11,15,20,0.92); backdrop-filter: blur(6px); min-width: 0; overflow: visible; }
        .app-shell-header-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-width: 0; }
        .app-shell-header-bottom { margin: 0 -18px -8px; min-width: 0; }
        .app-shell-content { padding: 18px 0; overflow-y: auto; overflow-x: hidden; min-width: 0; min-height: 0; -webkit-overflow-scrolling: touch; }
        .app-shell-content > .app-shell-main-inner { min-width: 0; }
        .app-shell-content > .app-shell-main-inner > * { min-width: 0; max-width: 100%; }
        @media (max-width: 1280px) {
          .app-shell-main-inner { padding: 0 14px; }
        }
        @media (max-width: 1100px) {
          .app-shell-main-inner { padding: 0 10px; }
        }
        @media (min-width: 1024px) {
          .app-shell-content { padding-top: 9px; }
        }
        .app-shell-footer { border-top: 1px solid rgba(255,255,255,0.06); padding: 10px 18px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .app-shell-brand { display: grid; justify-items: stretch; gap: 2px; padding: 2px 8px 0; }
        .app-shell-brand-logo-wrap { width: 124px; height: 124px; justify-self: center; display: grid; place-items: center; background: transparent; }
        .app-shell-brand-logo { width: 100%; height: 100%; object-fit: contain; background: transparent; }
        .app-shell-brand-name { margin: 0; text-align: left; font-size: 1.05rem; font-weight: 600; line-height: 1.2; color: rgba(248,250,252,0.96); letter-spacing: -0.01em; }
        .app-shell-brand-tagline { margin: 0; text-align: left; font-size: var(--text-label); font-weight: 500; color: rgba(229,231,235,0.62); line-height: 1.22; }
        .app-shell-nav { display: grid; gap: 2px; align-content: start; margin-top: 18px; }
        .app-shell-nav-divider { border: 0; border-top: 1px solid rgba(148,163,184,0.24); margin: 10px 10px 10px; }
        .app-shell-primary-action { width: calc(100% - 16px); margin: 0 8px; border: 1px solid rgba(59,130,246,0.5); background: rgba(59,130,246,0.15); color: rgba(239,246,255,0.96); border-radius: 12px; min-height: 58px; padding: 0.82rem 0.86rem; text-align: left; font-weight: 760; font-size: 0.98rem; display: flex; align-items: center; gap: 0.58rem; cursor: pointer; transition: background 120ms ease, border-color 120ms ease, transform 60ms ease; }
        .profile-dropdown-action { width: 100%; text-align: left; border: none; background: transparent; padding: 7px 10px; border-radius: 8px; cursor: pointer; font-size: 13px; transition: background 80ms ease; }
        .profile-dropdown-action:hover { background: rgba(255,255,255,0.07); }
        .mobile-menu-btn { display: none; flex-shrink: 0; }
        .app-shell-sidebar-overlay { display: none; position: fixed; inset: 0; z-index: 99; background: rgba(0,0,0,0.55); }
        .sprint-card-full { display: none !important; }
        .sprint-card-compact { display: none !important; }
        @media (min-width: 1024px) {
          .sprint-card-full { display: grid !important; }
        }
        @media (max-width: 1023px) {
          .sprint-card-compact { display: inline-flex !important; align-items: center; gap: 5px; max-width: min(220px, 40vw); overflow: hidden; }
        }
        /* ── Mobile bottom nav ───────────────────────────────────────────── */
        .mobile-bottom-nav {
          display: none;
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 200;
          background: rgba(11,15,20,0.97);
          border-top: 1px solid ${brand.border.subtle};
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          padding: 6px 8px max(6px, env(safe-area-inset-bottom));
          align-items: flex-end;
          justify-content: space-around;
          gap: 2px;
        }
        .mobile-bottom-nav-tab {
          display: flex; flex-direction: column; align-items: center;
          gap: 3px; border: none; background: transparent; cursor: pointer;
          color: rgba(229,231,235,0.45); font-size: 10px; font-weight: 600;
          padding: 5px 6px 2px; border-radius: 10px; flex: 1;
          transition: color 120ms ease;
          font-family: var(--font-sans);
          letter-spacing: 0.01em;
        }
        .mobile-bottom-nav-tab.active { color: ${brand.accent.primary}; }
        .mobile-bottom-nav-tab:active { opacity: 0.75; }
        .mobile-bottom-nav-log-btn {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: 2px; border: none; cursor: pointer;
          background: ${brand.accent.primary};
          color: white; font-size: 9px; font-weight: 800;
          width: 52px; height: 52px; border-radius: 999px;
          margin-bottom: 4px; flex-shrink: 0;
          box-shadow: 0 4px 14px rgba(59,130,246,0.45);
          font-family: var(--font-sans); letter-spacing: 0.04em; text-transform: uppercase;
        }
        .mobile-bottom-nav-log-btn:active { transform: scale(0.94); }
        @media (max-width: 700px) {
          .app-shell { grid-template-columns: 1fr; }
          .app-shell-sidebar { display: none; }
          .app-shell-sidebar-overlay { display: none !important; }
          .mobile-menu-btn { display: none !important; }
          .sprint-card-compact { max-width: min(180px, 38vw) !important; }
          .app-shell-main-inner { max-width: none; padding: 0 12px; }
          .app-shell-content { padding: 12px 0 ${hasMobileNav ? "76px" : "12px"}; overflow-x: hidden; }
          .account-badge-name { display: none !important; }
          .mobile-bottom-nav { display: flex; }
          .app-shell-header { padding-top: max(6px, env(safe-area-inset-top)); padding-bottom: 4px; gap: 6px; }
        }
      `}</style>

      <div
        className={`app-shell-sidebar-overlay${sidebarOpen ? " mobile-open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside className={`app-shell-sidebar${sidebarOpen ? " mobile-open" : ""}`}>
        <div className="app-shell-brand">
          <div className="app-shell-brand-logo-wrap">
            <img className="app-shell-brand-logo" src="/app-icon-trans.png" alt="WeightPlanner Pro logo" />
          </div>
          <p className="app-shell-brand-name">WeightPlanner Pro</p>
          <p className="app-shell-brand-tagline">Physics-based planning for cut, bulk, and maintenance.</p>
        </div>

        <nav className="app-shell-nav">
          {navItems.map((item) => {
            const active = activeNavKey === item.key;
            return (
              <button
                key={item.key}
                onClick={() => { onSelectNav(item.key); setSidebarOpen(false); }}
                style={{
                  border: "none",
                  borderLeft: active ? `3px solid ${brand.accent.primary}` : "3px solid transparent",
                  background: active ? "rgba(59,130,246,0.14)" : "transparent",
                  color: active ? "#f8fafc" : "rgba(229,231,235,0.9)",
                  borderRadius: 0,
                  padding: "14px 13px",
                  minHeight: 60,
                  textAlign: "left",
                  fontWeight: 600,
                  fontSize: 17,
                  cursor: "pointer",
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  transition: "background 120ms ease, color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "rgba(59,130,246,0.1)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: 0.95 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>
        {sidebarPrimaryAction ? (
          <div>
            <hr className="app-shell-nav-divider" />
            <button
              type="button"
              className="app-shell-primary-action"
              onClick={() => { sidebarPrimaryAction.onPress(); setSidebarOpen(false); }}
            >
              <span style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: 0.96 }}>
                {sidebarPrimaryAction.icon ?? (
                  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, stroke: "currentColor", strokeWidth: 2.5, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }}>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                )}
              </span>
              <span>{sidebarPrimaryAction.label}</span>
            </button>
          </div>
        ) : null}
      </aside>

      <div className="app-shell-main">
        <header className="app-shell-header">
          <div className="app-shell-main-inner">
          <div className="app-shell-header-top">
          {headerTopOverride ?? <>
          {/* Hamburger (desktop/tablet only — hidden on mobile when bottom nav is present) */}
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle navigation"
            style={{ border: `1px solid ${brand.border.strong}`, background: "rgba(255,255,255,0.04)", color: "rgba(229,231,235,0.9)", borderRadius: 8, width: 44, height: 44, alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
          >
            <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, stroke: "currentColor", strokeWidth: 2, fill: "none", strokeLinecap: "round" }}>
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* Left: sprint badge / page title */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "0 0 auto", overflow: "hidden" }}>
            {headerLeftOverride ?? (sprintSummaryCard ? (
              <>
                {/* Full card — hidden on mobile */}
                <button
                  type="button"
                  className="sprint-card-full"
                  onClick={sprintSummaryCard.onClick}
                  style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(51,65,85,0.52)", borderRadius: 12, padding: "6px 10px", color: "white", textAlign: "left", display: "grid", gap: 4, cursor: "pointer", minWidth: 0, width: "fit-content", fontFamily: "var(--font-sans)" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>Current Sprint</span>
                    <span style={{ border: `1px solid ${sprintSummaryCard.statusTone.border}`, background: sprintSummaryCard.statusTone.bg, color: sprintSummaryCard.statusTone.text, borderRadius: 999, padding: "2px 8px", fontWeight: 800, fontSize: 11 }}>{sprintSummaryCard.statusLabel}</span>
                    {sprintSummaryCard.phaseLabel ? <span style={{ border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.85)", borderRadius: 999, padding: "2px 8px", fontWeight: 750, fontSize: 11 }}>{sprintSummaryCard.phaseLabel}</span> : null}
                    <span style={{ marginLeft: "auto", color: "rgba(255,255,255,0.78)", fontWeight: 600, fontSize: 13 }}>{"View →"}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[sprintSummaryCard.startLabel, sprintSummaryCard.goalLabel, sprintSummaryCard.nowLabel, sprintSummaryCard.deltaLabel].map((label) => (
                      <span key={label} style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(30,41,59,0.6)", borderRadius: 999, padding: "4px 9px", fontWeight: 600, fontSize: "var(--text-metric)", fontVariantNumeric: "tabular-nums" }}>{label}</span>
                    ))}
                  </div>
                </button>
                {/* Compact pill — shown below 1024px */}
                <button
                  type="button"
                  className="sprint-card-compact"
                  onClick={sprintSummaryCard.onClick}
                  style={{ border: "1px solid rgba(255,255,255,0.14)", background: "rgba(51,65,85,0.52)", borderRadius: 999, padding: "5px 9px", color: "white", cursor: "pointer", fontFamily: "var(--font-sans)", flexShrink: 1, minWidth: 0 }}
                >
                  <span style={{ border: `1px solid ${sprintSummaryCard.statusTone.border}`, background: sprintSummaryCard.statusTone.bg, color: sprintSummaryCard.statusTone.text, borderRadius: 999, padding: "2px 7px", fontWeight: 800, fontSize: 11, flexShrink: 0 }}>{sprintSummaryCard.statusLabel}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(229,231,235,0.85)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>Sprint</span>
                  <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, flexShrink: 0 }}>›</span>
                </button>
              </>
            ) : (
              <>
                <div style={{ color: "rgba(229,231,235,0.72)", fontSize: 14 }}>Current Sprint</div>
                <div style={{ display: "inline-flex", gap: 8, alignItems: "center", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "6px 12px", background: "rgba(255,255,255,0.05)", minWidth: 0 }}>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}>{headerSprintLabel}</span>
                  {headerSprintModeTag ? <span style={{ color: "#86efac", fontSize: 12 }}>{headerSprintModeTag}</span> : null}
                </div>
              </>
            ))}
          </div>

          {/* Right: extras + gear + account badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {headerRightExtras}

            {onSettings && !hideSettingsButton && (
              <button
                onClick={onSettings}
                title="Settings"
                style={{ border: `1px solid ${brand.border.strong}`, background: "rgba(255,255,255,0.04)", color: "rgba(229,231,235,0.9)", borderRadius: 999, width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
              >
                <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, stroke: "currentColor", strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }}>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}

            {/* Account badge */}
            <div ref={profileRef} style={{ position: "relative" }}>
              <button
                className="profile-badge-btn"
                onClick={() => setProfileOpen((v) => !v)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  display: "inline-flex",
                  cursor: "pointer",
                  color: "white",
                }}
              >
                <AccountBadge
                  avatarKey={avatarKey}
                  initials={initials}
                  displayName={resolvedDisplayName}
                  syncStatus={syncStatus}
                  isAuthenticated={isAuthenticated}
                  showChevron
                />
              </button>

              {profileOpen && (
                <div style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  border: "1px solid rgba(255,255,255,0.11)",
                  background: brand.background.card,
                  borderRadius: 14,
                  minWidth: 220,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
                  overflow: "hidden",
                  zIndex: "var(--layer-account-dropdown)",
                }}>
                  {/* Status header */}
                  <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                    {isAuthenticated && profileEmail && (
                      <div style={{ fontSize: 12, color: "rgba(229,231,235,0.45)", marginBottom: 5, fontWeight: 500 }}>
                        {profileEmail}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <StatusDot color={dotColor} title={statusText} />
                      <span style={{ fontSize: 12, color: "rgba(229,231,235,0.75)", fontWeight: 650 }}>{statusText}</span>
                    </div>
                    {isAuthenticated && lastSyncAt && (
                      <div style={{ fontSize: 11, color: "rgba(229,231,235,0.32)", marginTop: 3 }}>
                        Last sync: {formatSyncTime(lastSyncAt)}
                      </div>
                    )}
                    {isAuthenticated && syncError && (
                      <div style={{ fontSize: 11, color: "#fca5a5", marginTop: 3 }}>
                        Last error: {syncError}
                      </div>
                    )}
                    {!isAuthenticated && (
                      <div style={{ fontSize: 11, color: "rgba(229,231,235,0.32)", marginTop: 3 }}>
                        Sign in to sync across devices.
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ padding: "6px 6px" }}>
                    {profileActions.map((item) => (
                      <button
                        key={item.key}
                        className="profile-dropdown-action"
                        disabled={item.disabled}
                        onClick={() => {
                          item.onSelect();
                          setProfileOpen(false);
                        }}
                        style={{
                          color: item.disabled
                            ? "rgba(229,231,235,0.35)"
                            : item.key === "logout" || item.key === "signout"
                            ? "#fca5a5"
                            : "rgba(229,231,235,0.9)",
                          cursor: item.disabled ? "not-allowed" : "pointer",
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </>}
          </div>
          </div>
          {headerBottomContent ? <div className="app-shell-header-bottom">{headerBottomContent}</div> : null}
        </header>

        <main ref={contentRef} className="app-shell-content" style={{ minWidth: 0 }}>
          <div className="app-shell-main-inner">{children}</div>
        </main>

        <footer className="app-shell-footer">
          {termsUrl ? (
            <a href={termsUrl} style={{ color: "rgba(229,231,235,0.3)", fontSize: 12, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Terms</a>
          ) : null}
          {privacyUrl ? (
            <a href={privacyUrl} style={{ color: "rgba(229,231,235,0.3)", fontSize: 12, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Privacy</a>
          ) : null}
          {supportUrl ? (
            <a href={supportUrl} style={{ color: "rgba(229,231,235,0.3)", fontSize: 12, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Support</a>
          ) : null}
          {appVersion ? (
            <span style={{ color: "rgba(229,231,235,0.25)", fontSize: 12 }}>Version {appVersion}</span>
          ) : null}
        </footer>

        {/* Mobile bottom navigation bar */}
        {hasMobileNav && (
          <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
            {leftNavItems.map((item) => (
              <button
                key={item.key}
                className={`mobile-bottom-nav-tab${activeMobileNavKey === item.key ? " active" : ""}`}
                onClick={() => onSelectNav(item.key)}
                aria-label={item.label}
              >
                <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{item.icon}</span>
                {item.label}
              </button>
            ))}

            <button
              className="mobile-bottom-nav-log-btn"
              onClick={mobileLogButton?.onPress}
              aria-label="Log daily entry"
            >
              <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, stroke: "currentColor", strokeWidth: 2.5, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }}>
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Log</span>
            </button>

            {rightNavItems.map((item) => (
              <button
                key={item.key}
                className={`mobile-bottom-nav-tab${activeMobileNavKey === item.key ? " active" : ""}`}
                onClick={() => onSelectNav(item.key)}
                aria-label={item.label}
              >
                <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}
